/**
 * WebXR immersive-ar + hit-test で、ビュー方向の実環境までの距離 (m) を取得する。
 * 対応端末（主に ARCore 等）でのみ有効。失敗時は何もせず bbox 推定に任せる。
 */

import { devConfig } from "./appConfig.js";
import { pushHardwareRangeSample, clearHardwareRangeSensor } from "./objectRange.js";

/** @type {XRSession | null} */
let xrSession = null;
/** @type {XRReferenceSpace | null} */
let xrHitSpace = null;
/** @type {XRHitTestSource | null} */
let xrHitSource = null;
/** @type {XRWebGLLayer | null} */
let xrGlLayer = null;
/** @type {WebGLRenderingContext | null} */
let xrGl = null;
/** @type {number} */
let xrHitTestFrameHandle = 0;

function disposeWebGl() {
  if (xrGl?.getExtension?.("WEBGL_lose_context")?.loseContext) {
    try {
      xrGl.getExtension("WEBGL_lose_context").loseContext();
    } catch {
      /* noop */
    }
  }
  xrGl = null;
  xrGlLayer = null;
}

/**
 * WebGL コンテキストと XRWebGLLayerを用意（一部実装で baseLayer 必須）
 * @param {XRSession} session
 */
function ensureXrBaseLayer(session) {
  if (xrGlLayer) return true;
  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl", { xrCompatible: true }) ||
    canvas.getContext("experimental-webgl", { xrCompatible: true });
  if (!gl) return false;
  xrGl = gl;
  try {
    xrGlLayer = new XRWebGLLayer(session, gl);
    session.updateRenderState({ baseLayer: xrGlLayer });
  } catch {
    disposeWebGl();
    return false;
  }
  return true;
}

/**
 * @param {XRSession} session
 * @param {DOMHighResTimeStamp} time
 * @param {XRFrame} frame
 */
function onXrFrame(session, time, frame) {
  if (!xrHitSource || !xrHitSpace) {
    session.requestAnimationFrame((t, f) => onXrFrame(session, t, f));
    return;
  }
  let distM = null;
  try {
    const hits = frame.getHitTestResults(xrHitSource);
    if (hits.length > 0) {
      const pose = hits[0].getPose(xrHitSpace);
      if (pose) {
        const { x, y, z } = pose.transform.position;
        distM = Math.hypot(x, y, z);
      }
    }
  } catch {
    distM = null;
  }
  if (distM != null && Number.isFinite(distM)) {
    pushHardwareRangeSample(distM);
  }
  xrHitTestFrameHandle = session.requestAnimationFrame((t, f) =>
    onXrFrame(session, t, f),
  );
}

/**
 * 実センサー経路の停止（カメラ停止前に呼ぶ）
 */
export function stopWebXrHitTestRangeSensor() {
  if (xrHitTestFrameHandle && xrSession) {
    try {
      xrSession.cancelAnimationFrame(xrHitTestFrameHandle);
    } catch {
      /* noop */
    }
  }
  xrHitTestFrameHandle = 0;
  xrHitSource = null;
  xrHitSpace = null;
  if (xrSession) {
    try {
      xrSession.end();
    } catch {
      /* noop */
    }
  }
  xrSession = null;
  disposeWebGl();
  clearHardwareRangeSensor();
}

/**
 * WebXR hit-test が使える場合のみセッションを開始する。
 * ユーザ操作の近傍で呼ぶこと（requestSession の制約）。
 */
export async function tryStartWebXrHitTestRangeSensor() {
  if (devConfig.detection.enableWebXrRangeSensor === false) return;
  if (xrSession) return;
  const xr = navigator.xr;
  if (!xr?.requestSession) return;

  let supported = false;
  try {
    supported = await xr.isSessionSupported("immersive-ar");
  } catch {
    return;
  }
  if (!supported) return;

  /** @type {XRSession | undefined} */
  let session;
  try {
    session = await xr.requestSession("immersive-ar", {
      requiredFeatures: ["hit-test"],
      optionalFeatures: ["dom-overlay"],
      domOverlay: { root: document.body },
    });
  } catch {
    try {
      session = await xr.requestSession("immersive-ar", {
        requiredFeatures: ["hit-test"],
      });
    } catch (e2) {
      console.debug("[webxrHitTestRange] session failed:", e2);
      return;
    }
  }
  if (!session) return;

  try {
    xrSession = session;

    if (!ensureXrBaseLayer(session)) {
      session.end();
      xrSession = null;
      return;
    }

    const viewerSpace = await session.requestReferenceSpace("viewer");
    xrHitSpace = viewerSpace;
    xrHitSource = await session.requestHitTestSource?.({ space: viewerSpace });
    if (!xrHitSource) {
      session.end();
      xrSession = null;
      xrHitSpace = null;
      disposeWebGl();
      return;
    }

    session.addEventListener("end", () => {
      xrHitTestFrameHandle = 0;
      xrHitSource = null;
      xrHitSpace = null;
      xrSession = null;
      disposeWebGl();
      clearHardwareRangeSensor();
    });

    xrHitTestFrameHandle = session.requestAnimationFrame((t, f) =>
      onXrFrame(session, t, f),
    );
  } catch (e) {
    console.debug("[webxrHitTestRange] setup failed:", e);
    stopWebXrHitTestRangeSensor();
  }
}
